import type { FlightDirection, FlightOptionModel } from '@elara/client-core/models/FlightOptionModel';
import type { FlightSegmentModel } from '@elara/client-core/models/FlightSegmentModel';
import type { AirlineInfo } from '@elara/domain/models/schemas/flight-schemas';
import type { MessageRef } from '@elara/domain/models/schemas/shortlist-schemas';

import type { BaseDetailCardLayoutProps } from './DetailCardLayout.js';

import { formatDuration } from '@elara/client-core/lib/date-utils';
import { cn, pluralize } from '@elara/client-core/lib/utils';

import { useItineraryState, useNavigate, useTripsState } from '#states/hooks.js';
import { useCompactGuideActions } from '#ui/guides/guide-header-actions.js';
import { Avatar, AvatarFallback, AvatarImage, Badge, Button, ItineraryButton, ShortlistButton } from '#ui/index.js';
import { IconSizes, MaterialIcon } from '#ui/MaterialIcon.js';
import { PriceStatusIndicator } from '#ui/PriceStatusIndicator.js';

import { BASE_DETAIL_CARD_CONTENT_CLASSES } from './DetailCardLayout.js';

export function AirlineLogo({ airline, className }: { airline: AirlineInfo | undefined; className?: string }) {
  const logoUrl = airline?.logo_url;
  const fallbackText = airline?.name || airline?.icao_code || '';

  return (
    <Avatar className={cn('rounded-none', className)}>
      {logoUrl && <AvatarImage src={logoUrl} alt={fallbackText} loading="lazy" />}
      <AvatarFallback className="text-xs font-medium uppercase rounded-sm bg-secondary text-secondary-foreground">
        {fallbackText.slice(0, 2)}
      </AvatarFallback>
    </Avatar>
  );
}

export function FlightActions({
  flightOption,
  messageRef,
  variant: variantProp,
}: {
  flightOption: FlightOptionModel;
  messageRef?: MessageRef;
  /** Defaults to `icon` in a guide panel's floating header (see `guide-header-actions`), else `default`. */
  variant?: 'icon' | 'default';
}) {
  const compact = useCompactGuideActions();
  const variant = variantProp ?? (compact ? 'icon' : 'default');
  return (
    <>
      <ShortlistButton
        itemType="flight"
        title={flightOption.displayTitle('all')}
        referenceId={flightOption.id}
        messageRef={messageRef}
        variant={'icon'}
      />
      <ItineraryButton
        itemType="flight"
        referenceId={flightOption.id}
        title={flightOption.displayTitle('all')}
        messageRef={messageRef}
        variant={variant}
        buttonVariant="outline"
      />
      <FlightBookButton flightOption={flightOption} variant={variant} />
      <FlightViewBookingButton flightOption={flightOption} variant={variant} />
    </>
  );
}

/**
 * "View booking" action shown when an itinerary item for this flight is already
 * marked as booked. Opens the read-only booking-details dialog (the same
 * surface as the bookings tab), not the checkout flow. Mutually exclusive with
 * FlightBookButton.
 */
export function FlightViewBookingButton({
  flightOption,
  variant = 'default',
}: {
  flightOption: FlightOptionModel;
  variant?: 'icon' | 'default';
}) {
  const itineraryState = useItineraryState();

  const bookedItem = itineraryState.items.find(
    (item) => item.type === 'flight' && item.referenceId === flightOption.id && item.isBooked,
  );
  if (!bookedItem) return null;

  const handleView = () => itineraryState.openViewBookingDialog(bookedItem);

  if (variant === 'icon') {
    return (
      <Button variant="icon" size="icon-sm" tooltip="View booking" onClick={handleView}>
        <MaterialIcon icon="local_activity" size={IconSizes.sm} />
      </Button>
    );
  }

  return (
    <Button variant="outline" onClick={handleView}>
      <MaterialIcon icon="local_activity" size={IconSizes.sm} />
      <span>View booking</span>
    </Button>
  );
}

/**
 * "Book" button for a flight option. Hidden when the flight has no booking
 * token / segments (so checkout would have nothing to show), or when an
 * itinerary item for this option is already marked as booked. Renders icon-
 * only with a tooltip when `variant="icon"` (compact contexts like shortlist
 * cards), otherwise as a full button with label.
 */
export function FlightBookButton({
  flightOption,
  variant = 'default',
}: {
  flightOption: FlightOptionModel;
  variant?: 'icon' | 'default';
}) {
  const tripsState = useTripsState();
  const itineraryState = useItineraryState();
  const { navigate } = useNavigate();

  const hasBookingOptions = flightOption.roundTrip
    ? !!flightOption.returnSegments && flightOption.returnSegments.length > 0
    : !!flightOption.outboundSegments && flightOption.outboundSegments.length > 0;
  if (!hasBookingOptions || !flightOption.bookingToken) return null;

  const alreadyBooked = itineraryState.items.some(
    (item) => item.type === 'flight' && item.referenceId === flightOption.id && item.isBooked,
  );
  if (alreadyBooked) return null;

  function handleBook(): void {
    const tripId = tripsState.current?.id;
    if (!tripId) return;
    navigate(`/checkout/flight/${tripId}/${flightOption.id}`);
  }

  if (variant === 'icon') {
    return (
      <Button variant="icon" size="icon-sm" tooltip="Book flight" onClick={handleBook}>
        <MaterialIcon icon="shopping_cart_checkout" size={IconSizes.sm} />
      </Button>
    );
  }

  return (
    <Button onClick={handleBook}>
      <MaterialIcon icon="shopping_cart_checkout" size={IconSizes.sm} />
      <span>Book flight</span>
    </Button>
  );
}

export function FlightSummaryHeader({
  flightOption,
  direction,
  className,
}: {
  flightOption: FlightOptionModel;
  direction: FlightDirection;
  className?: string;
}) {
  const primaryAirline = flightOption.primaryAirline(direction);
  const firstFlight = flightOption.flightsForDirection(direction)[0];
  const flightNumber = firstFlight?.flightNumber;

  return (
    <div className={cn('flex items-center gap-2 text-xs text-muted-foreground truncate', className)}>
      {primaryAirline && (
        <>
          <AirlineLogo airline={primaryAirline} className="h-5 w-5" />
          <div className="[.sl-card_&]:hidden truncate">{flightOption.formattedAirlineNames(direction)}</div>
        </>
      )}

      {flightNumber && (
        <>
          <div className="[.sl-card_&]:hidden">·</div>
          <div className="truncate">{flightNumber}</div>
        </>
      )}

      {flightOption.formattedTravelClass(direction) && (
        <>
          <div>·</div>
          <div className="truncate">{flightOption.formattedTravelClass(direction)}</div>
        </>
      )}

      {flightOption.roundTrip && (!flightOption.returnSegments || flightOption.returnSegments.length === 0) && (
        <>
          <div>·</div>
          <div className="truncate">Round trip</div>
        </>
      )}
    </div>
  );
}

export function FlightSummary({
  segments,
  displayStops = true,
  displayDates = true,
  className,
}: {
  segments: FlightSegmentModel[];
  displayStops?: boolean;
  displayDates?: boolean;
  className?: string;
}) {
  const firstFlight = segments[0];
  const lastFlight = segments.at(-1);
  const hasConnections = segments.length > 1;
  const stopCount = Math.max(0, segments.length - 1);
  const totalTravelTime =
    segments.length === 0
      ? 0
      : segments.reduce((acc, s) => acc + (s.duration ?? 0), 0) +
        (segments.length <= 1
          ? 0
          : segments.slice(0, -1).reduce((acc, cur, i) => acc + cur.getLayoverTime(segments[i + 1]!), 0));

  return (
    <div className={cn(className, 'flex items-start gap-4 @md:gap-6 w-full')}>
      {/* Departure */}
      <div className="flex flex-col gap-1">
        <div className="font-medium whitespace-nowrap [.sl-card_&]:text-sm [.ii-card_&]:text-sm [.bv-card_&]:text-sm">
          {firstFlight ? firstFlight.formattedDepartureTime : 'N/A'}
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
          <div>{firstFlight?.originCode}</div>
          {displayDates && (
            <>
              <div>·</div>
              <div>{firstFlight?.formattedDepartureDate}</div>
            </>
          )}
        </div>
      </div>
      {/* Travel time */}
      <div className="flex-1 flex flex-col items-center gap-1.5 [.sl-card_&]:gap-0.5 [.ii-card_&]:gap-0.5 [.bv-card_&]:gap-0.5">
        <div className="w-full flex items-center">
          <div className="w-4 h-4 rounded-full border"></div>
          <div className="w-px flex-1 border-t"></div>
          {displayStops ? (
            <>
              {hasConnections ? (
                <Badge variant="outline" className="whitespace-nowrap">
                  {stopCount} {pluralize(stopCount, 'stop')}
                </Badge>
              ) : (
                <Badge variant="secondary">Direct</Badge>
              )}
            </>
          ) : (
            <Badge variant="outline" className="font-normal text-muted-foreground">
              {formatDuration(totalTravelTime)}
            </Badge>
          )}
          <div className="w-px flex-1 border-t"></div>
          <div className="w-4 h-4 rounded-full border"></div>
        </div>

        {displayStops && totalTravelTime > 0 && (
          <div className="text-xs text-muted-foreground">{formatDuration(totalTravelTime)}</div>
        )}
      </div>
      {/* Arrival */}
      <div className="flex flex-col items-end gap-1">
        <div className="font-medium whitespace-nowrap [.sl-card_&]:text-sm [.ii-card_&]:text-sm [.bv-card_&]:text-sm">
          {lastFlight ? lastFlight.formattedArrivalTime : 'N/A'}
          {lastFlight?.isOvernight && <sup className="text-xs text-muted-foreground ml-0.5 font-normal">+1</sup>}
        </div>
        <div className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap">
          <div>{lastFlight?.destinationCode}</div>
          {displayDates && (
            <>
              <div>·</div>
              <div>{lastFlight?.formattedArrivalDate}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
export interface FlightCardProps extends BaseDetailCardLayoutProps {
  flightOption: FlightOptionModel;
  /** False in the hover popover: a glance at a link is no place to recheck a price; the guide is. */
  showPriceStatus?: boolean;
}

export function FlightCard({
  flightOption,
  size = 'default',
  className,
  actions,
  onClick,
  showTotals,
  displayPricing = true,
  showPriceStatus: allowPriceStatus = true,
}: FlightCardProps) {
  const itineraryItems = useItineraryState().items;

  if (!flightOption) return null;

  const bookingStatus = itineraryItems.find((item) => item.type === 'flight' && item.referenceId === flightOption.id)
    ?.booking?.status;

  const isUnavailable = !flightOption.isAvailable;
  // Dims the card's CONTENT, section by section, rather than the card as a whole. A blanket wrapper
  // would take the "Sold out" badge down with it — opacity applies to a subtree and a child cannot
  // exceed its parent's — and the badge is the one thing on a sold-out card that must stay legible.
  const dimmed = isUnavailable ? 'opacity-50' : undefined;
  const showPriceStatus =
    allowPriceStatus && !isUnavailable && bookingStatus !== 'approved' && bookingStatus !== 'booked';

  const priceIndicator = showPriceStatus ? (
    <PriceStatusIndicator
      type="flight"
      model={flightOption}
      priceDivisor={flightOption.totalPaidPassengers}
      priceUnit="per passenger"
    />
  ) : null;

  const card =
    size === 'lg' ? (
      <div
        className={cn(
          'relative grid grid-cols-1 grid-row-1 gap-4 p-4 cursor-pointer',
          displayPricing ? '@md:grid-cols-3' : '@md:grid-cols-2',
          className,
        )}
        onClick={isUnavailable ? undefined : onClick}
      >
        <div className={cn('col-span-1 row-span-1 @md:col-span-2 @md:row-span-1', dimmed)}>
          <FlightSummaryHeader flightOption={flightOption} direction="outbound" className="mb-3 mr-28 @md:mr-0" />
          <FlightSummary segments={flightOption.flightsForDirection('outbound')} />

          {flightOption.returnSegments && flightOption.returnSegments.length > 0 && (
            <>
              <FlightSummaryHeader flightOption={flightOption} direction="return" className="mt-4 mb-3" />
              <FlightSummary segments={flightOption.flightsForDirection('return')} />
            </>
          )}
        </div>

        {displayPricing && (
          <div
            className={cn(
              'col-span-1 row-span-1 @md:col-span-2 @md:row-span-1 @md:col-start-3 flex @md:flex-col @md:items-end @md:justify-end',
              flightOption.returnSegments && flightOption.returnSegments.length > 0 ? '@md:border-l' : '',
            )}
          >
            <div className="w-full flex justify-between gap-1 flex-col @md:items-end">
              {/* Above the price, not a banner under the card: a sold-out flight's price is the number
                  the badge qualifies, so the two belong together. */}
              {isUnavailable && (
                <Badge variant="destructive" size="lg" className="mb-1">
                  Sold out
                </Badge>
              )}
              <div
                className={cn(
                  'flex items-center gap-1.5 font-medium text-foreground text-sm @md:text-base',
                  isUnavailable && 'line-through',
                )}
              >
                {flightOption.formattedPricePerPerson}
              </div>
              {/* Struck as a whole: `costDescription` is one string ("$8,200 for 2 passengers"), so
                  there is no amount to isolate — and a total that is no longer obtainable should not
                  read as still standing. */}
              <div className={cn('text-xs text-muted-foreground whitespace-nowrap', isUnavailable && 'line-through')}>
                {flightOption.costDescription}
              </div>
            </div>
          </div>
        )}

        {flightOption.hasActions && !isUnavailable && actions && (
          <div className="absolute top-2 right-2 z-10" onClick={(e) => e.stopPropagation()}>
            {actions}
          </div>
        )}
      </div>
    ) : (
      <div
        // `[.sl-card_&]:pb-3` for the same reason as `DetailCardLayout`'s default branch: in the
        // shortlist the even `p-3` leaves the last line sitting all but on the card's edge.
        className={cn(BASE_DETAIL_CARD_CONTENT_CLASSES, 'gap-4', '[.sl-card_&]:pb-3', className)}
        onClick={isUnavailable ? undefined : onClick}
      >
        <div className={cn('flex flex-col gap-4', dimmed)}>
          <FlightSummaryHeader flightOption={flightOption} direction="all" />
          <FlightSummary segments={flightOption.flightsForDirection('outbound')} className="sl-card" />

          {flightOption.returnSegments && flightOption.returnSegments.length > 0 && (
            <FlightSummary segments={flightOption.flightsForDirection('return')} className="sl-card" />
          )}
        </div>

        {displayPricing && (
          <div className="w-full flex flex-col items-start gap-0.5">
            {/* Baseline, so the price status beside the price reads on the same line of text as it. */}
            <div className="flex items-baseline gap-1.5">
              {/* No staleness styling on the price — the `Refresh` affordance beside it says so. */}
              <div className="flex items-baseline gap-1">
                {/* Struck when sold out — the amount only; `/ passenger` is already a sibling, and a
                    text decoration cannot be opted out of by a descendant. */}
                <div
                  className={cn(
                    'font-medium text-foreground text-sm @md:text-base',
                    isUnavailable && 'line-through',
                    dimmed,
                  )}
                >
                  {flightOption.formattedPricePerPerson}
                </div>
                <div className={cn('text-2xs', dimmed)}>/ passenger</div>
              </div>
              {/* Beside the price, not above it: this is the compact row the shortlist uses, where a
                  stacked badge costs a whole line and pushes the card taller. `sm` for the same
                  reason — the `lg` pill is sized for the roomier card. */}
              {isUnavailable && (
                <Badge variant="destructive" size="sm">
                  Sold out
                </Badge>
              )}
              {priceIndicator}
            </div>
            {showTotals && (
              <div className={cn('text-2xs text-muted-foreground whitespace-nowrap', isUnavailable && 'line-through')}>
                {flightOption.formattedTotalPrice} for {flightOption.totalPassengers}{' '}
                {pluralize(flightOption.totalPassengers, 'passenger')}
              </div>
            )}
          </div>
        )}

        {flightOption.hasActions && !isUnavailable && actions && (
          <div className="absolute top-1 right-1 z-10" onClick={(e) => e.stopPropagation()}>
            {actions}
          </div>
        )}
      </div>
    );

  // The card dims its own sections (see `dimmed`), so an unavailable flight needs no wrapper — only
  // its actions put back, since the card suppresses them above when unavailable.
  if (isUnavailable) {
    return (
      <div className="relative">
        {card}
        {actions && (
          <div className="absolute top-1 right-1 z-10" onClick={(e) => e.stopPropagation()}>
            {actions}
          </div>
        )}
      </div>
    );
  }

  return card;
}
